import React, { useState, useEffect } from 'react';
import Modal, { ModalDirectionOptions } from '../../components/Modal';
import {
  getDoseByWeightWithMaxLimitFormatted,
  calcAmountToAdminister,
  administrationUnitToDosage,
  mlToDosage,
  formatNumber
} from '../../Resus/medicationUtils';
import { useResusContext } from '../../Resus/ResusContext';
import { AdministrationDetails, Drug } from '../types';
import { useTranslation } from 'react-i18next';

interface MedicationConfirmationDialogProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  drug: Drug;
  onConfirm: (administrationDetails?: AdministrationDetails) => void;
}

const getTitleString = (drug: Drug, t: any): string => {
  if (!drug.constant_dose) {
    return t('medicationDialogs.medicationConfirmation.changeDefaultDoseWithWeight');
  }
  return t('medicationDialogs.medicationConfirmation.changeDefaultDose');
};

const MedicationConfirmationDialog: React.FC<MedicationConfirmationDialogProps> = ({
  isOpen,
  setIsOpen,
  drug,
  onConfirm,
}) => {
  const { weight } = useResusContext();
  const { t } = useTranslation(['resus']);
  const shouldShowMlInput = !!drug.concentration; // show ml only if there is concentration
  const [exceedsMaxDose, setExceedsMaxDose] = useState(false);
  const [primaryValue, setPrimaryValue] = useState('');
  const [mlValue, setMlValue] = useState('');

  const checkExceedsMaxDose = (value: string, drug: Drug): boolean => {
    if (!drug.maxDose) return false;
    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue > Number(drug.maxDose);
  };

  useEffect(() => {
    if (isOpen) {
      const calculatedDose = getDoseByWeightWithMaxLimitFormatted(drug, weight);
      setPrimaryValue(calculatedDose);
      setMlValue(shouldShowMlInput ? calcAmountToAdminister(drug, weight) : '');
      setExceedsMaxDose(checkExceedsMaxDose(calculatedDose, drug));
    }
  }, [drug, isOpen, weight, shouldShowMlInput]);

  const formatInputValue = (value: string): string => {
    if (value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return formatNumber(numValue);
  };

  const handlePrimaryValueChange = (value: string) => {
    setExceedsMaxDose(checkExceedsMaxDose(value, drug));

    if (shouldShowMlInput) {
      // since the context is that the doctor explicitly wants to overwrite the value
      // we disragrd min and max dose. so we delete them from the object.
      const { minDose, maxDose, ...drugNowMinMax } = drug;
      const newValues = administrationUnitToDosage(drugNowMinMax, weight, value);
      setPrimaryValue(value)
      setMlValue(formatInputValue(newValues.mlValue));
    } else {
      setPrimaryValue(value);
      setMlValue('');
    }
  };

  const handleMlValueChange = (value: string) => {
    if (shouldShowMlInput) {
      const newValues = mlToDosage(drug, value);
      setExceedsMaxDose(checkExceedsMaxDose(newValues.primaryValue, drug));
      setPrimaryValue(formatInputValue(newValues.primaryValue));
      setMlValue(value);
    }
  };

  const isEndotracheal = !drug.isDrip && drug.howToGive?.toLowerCase().includes('endotracheal');

  const handleConfirm = () => {
    onConfirm(createAdministartionDetails());
    setIsOpen(false);
  };

  const createAdministartionDetails = (): AdministrationDetails => {
    return {
      medicalSectionLog: formatMedicationSectionLog(),
      resusLog: formatResusLog()
    }
  };

  const formatMedicationSectionLog = (): string => {
    const defaultPrimaryValue = getDoseByWeightWithMaxLimitFormatted(drug, weight);
    if (drug.type === "mass" || drug.type === "fluid") {
      if (primaryValue == defaultPrimaryValue) {
        return '';
      }
      return `${primaryValue} ${drug.dose_unit}`;
    }

    // For regular drugs, check both primary value and ml value
    const defaultMlValue = calcAmountToAdminister(drug, weight);

    if (primaryValue === defaultPrimaryValue && mlValue === defaultMlValue) {
      return '';
    }

    const parts: string[] = [];

    if (primaryValue) {
      parts.push(`${primaryValue} ${drug.dose_unit}`);
    }

    if (mlValue) {
      parts.push(`${mlValue} ml`);
    }
    return parts.join(", ");
  };

  const formatResusLog = (): string => {
    if ("type" in drug && (drug.type === "mass" || drug.type === "fluid")) {
      return `${drug.name}: ${primaryValue} ${drug.dose_unit}`;
    }
    const parts = [drug.name + ":"];
    if (primaryValue) {
      parts.push(`${primaryValue} ${drug.dose_unit}`);
    }
    if (mlValue) {
      parts.push(`${mlValue} ml`);
    }
    return parts.join(" ");
  };

  const numberInputSteps = (doseUnit?: string): number => {
    if (!doseUnit) return 1;
    if (doseUnit == "ml") return 0.1;
    if (doseUnit == "units") return 1;
    else return 0.1;
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t('medicationDialogs.medicationConfirmation.title')}
      direction={ModalDirectionOptions.RTL}
      primaryButton={{
        text: t('medicationDialogs.medicationConfirmation.confirm'),
        onClick: handleConfirm,
      }}
      secondaryButton={{
        text: t('medicationDialogs.medicationConfirmation.cancel'),
        onClick: () => setIsOpen(false),
      }}
    >
      <div style={{ textAlign: 'right', direction: 'rtl' }}>
        {isEndotracheal && (
          <div style={{ marginBottom: '20px' }}>
            <span dangerouslySetInnerHTML={{ __html: t('medicationDialogs.medicationConfirmation.endotrachealNote') }} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ marginBottom: '15px' }}>
            <div style={{ marginBottom: shouldShowMlInput ? '8px' : '0' }}>
              {getTitleString(drug, t)}
            </div>
            {shouldShowMlInput && (
              <div style={{ fontSize: '0.9em' }}>
                {t('medicationDialogs.medicationConfirmation.unitsUpdateNote')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '120px', flexShrink: 0 }}>
              {t('medicationDialogs.medicationConfirmation.doseInUnit', { unit: drug.dose_unit })}
            </div>
            <input
              type="number"
              value={primaryValue}
              onChange={(e) => handlePrimaryValueChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              step={numberInputSteps(drug.dose_unit)}
              style={{
                width: '120px',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
          </div>

          {shouldShowMlInput && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '120px', flexShrink: 0 }}>
                {t('medicationDialogs.medicationConfirmation.doseInMl')}
              </div>
              <input
                type="number"
                value={mlValue}
                onChange={(e) => handleMlValueChange(e.target.value)}
                onFocus={(e) => e.target.select()}
                step="0.1"
                style={{
                  width: '120px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
            </div>
          )}

          {exceedsMaxDose && drug.maxDose && (
            <div style={{ color: 'red' }}>
              {t('medicationDialogs.medicationConfirmation.exceedsMaxDose', {
                maxDose: drug.maxDose,
                unit: drug.dose_unit
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MedicationConfirmationDialog;